<template>
  <v-container fluid>
    <toolbar mode="application" :item="application">
      <v-spacer/>
      <template v-if="prediction">
        {{ prediction.name }}
        <v-btn icon @click="uuid=null"><v-icon>mdi-close</v-icon></v-btn>
      </template>
    </toolbar>
    <v-card v-if="prediction">
      <v-card-text>
        <v-row>
          <v-col cols="6">
            <v-card flat tile>
              <v-toolbar flat tile dense>
                <v-toolbar-title>MAP</v-toolbar-title>
                <v-spacer/>
              </v-toolbar>
              <v-card-text>
                <vue-plotly
                  v-if="charts.map.data.length"
                  class="full"
                  :data="charts.map.data"
                  :layout="charts.map.layout"
                  :options="charts.map.options"
                  @click="onMapClick"
                  @selected="onMapSelect"
                  @deselect="onMapDeselect"
                />
              </v-card-text>
            </v-card>
            <v-card flat tile :loading="dataframe.table.length == 0">
              <v-divider />
              <v-toolbar flat tile dense>
                <v-toolbar-title> Wells (N={{ wells_t }})</v-toolbar-title>
                <v-spacer/>
                <v-text-field
                  v-model="dataframe.search"
                  prepend-icon="mdi-magnify"
                  hide-details
                  clearable
                  dense
                ></v-text-field>
                <dlg-filter-wells :filters="filters" @submitted="onFilterWells"></dlg-filter-wells>
                <ax-button
                  icon="mdi-content-save-outline"
                  tooltip="Save current selection to group"
                  @clicked="onSaveGroup"
                  :disabled="dataframe.selected.length == 0"
                ></ax-button>
                <dlg-rename-group
                  v-if="selectedGroup"
                  :group="selectedGroup"
                  :key="groups.selected"
                  @submitted="onRenameGroup"
                ></dlg-rename-group>
              </v-toolbar>
              <v-card-text class="pt-0">
                <v-list v-if="groups.list.length">
                  <v-chip-group v-model="groups.selected" column>
                    <v-chip
                      v-for="group in groups.list"
                      :key="group.id"
                      :value="group.id"
                      close
                      active-class="primary"
                      @click:close="onDeleteGroup(group.id)"
                    >
                      <v-avatar left color="white grey--text text--darken-4">{{ group.wells.length }}</v-avatar>
                      {{ group.name }}
                    </v-chip>
                  </v-chip-group>
                </v-list>
                <p class="py-3" v-else>You have not created any groups</p>


                <v-data-table
                  height="50vh"
                  fixed-header
                  class="text-no-wrap hover"
                  :headers="dataframe.headers"
                  :items="dataframe.table"
                  :item-key="prediction.config.api"
                  v-model="dataframe.selected"
                  show-select
                  @click:row="onRowClick"
                  dense
                  hide-default-footer
                  disable-pagination
                  :single-select="false"
                  :search="dataframe.search"
                  :custom-filter="customFilter"
                >
                  <template v-slot:[`item.oil`]="{ item }">{{ item.oil | numberFormat((round = true)) }}</template>
                  <template v-slot:[`item.gas`]="{ item }">{{ item.gas | numberFormat((round = true)) }}</template>
                  <template v-slot:[`item.water`]="{ item }">{{ item.water | numberFormat((round = true)) }}</template>
                  <!-- <template v-slot:[`item.actions`]="{ item }">
                    <v-icon small class="mr-2" @click="onEditWell(item)">mdi-pencil</v-icon>
                    <v-icon small @click="onDeleteWell(item)">mdi-delete</v-icon>
                  </template> -->
                </v-data-table>
              </v-card-text>
            </v-card>
          </v-col>
          <v-col cols="6">
            <template v-if="dataframe.selected.length > 0">
              <v-card flat tile>
                <v-toolbar flat tile dense>
                  <v-toolbar-title>Analyzing production of n={{ wells_n }} {{ pluralize('well', wells_n) }}</v-toolbar-title>
                  <v-spacer/>
                  <ax-menu
                    icon="mdi-target"
                    title="Prediction Target"
                    :options="targets.options"
                    :default="targets.selected"
                    model="targets.selected"
                    @changed="onMenuChange"
                  />
                  <ax-menu
                    icon="mdi-axis-arrow"
                    title="Chart Axes"
                    :options="axis_types.options"
                    :default="axis_types.selected"
                    model="axis_types.selected"
                    @changed="onMenuChange"
                  ></ax-menu>
                  <ax-menu
                    icon="mdi-calendar-outline"
                    title="Switch time scales"
                    :options="timescale.options"
                    :default="timescale.selected"
                    model="timescale.selected"
                    @changed="onMenuChange"
                  />
                  <ax-menu
                    :mandatory="false"
                    icon="mdi-format-columns"
                    title="Analysis Dimensions"
                    :options="dimensions"
                    :default="dimension"
                    model="dimension"
                    @changed="onMenuChange"
                  />
                  
                </v-toolbar>
                <v-card-title>Estimated Ultimate Recovery (EUR)</v-card-title>
                <v-card-subtitle>Declined forecast for up to 600 months (50 years) of production</v-card-subtitle>
                <v-card-text>
                  <v-data-table
                    :loading="statistics.table.length == 0"
                    fixed-header
                    class="text-no-wrap hover"
                    :headers="statistics.headers"
                    :items="statistics.table"
                    :item-key="prediction.config.api"
                    dense
                    hide-default-footer
                    disable-pagination
                    disable-filtering
                    disable-sort
                  >
                    <template v-slot:[`item.p10`]="{ item }">{{ item.p10 | numberFormat((round = true)) }}</template>
                    <template v-slot:[`item.p50`]="{ item }">{{ item.p50 | numberFormat((round = true)) }}</template>
                    <template v-slot:[`item.p90`]="{ item }">{{ item.p90 | numberFormat((round = true)) }}</template>
                    <template v-slot:[`item.w10`]="{ item }">{{ item.w10 | numberFormat((round = true)) }}</template>
                    <template v-slot:[`item.w50`]="{ item }">{{ item.w50 | numberFormat((round = true)) }}</template>
                    <template v-slot:[`item.w90`]="{ item }">{{ item.w90 | numberFormat((round = true)) }}</template>
                    <template v-slot:[`item.avg`]="{ item }">{{ item.avg | numberFormat((round = true)) }}</template>
                    <template v-slot:[`item.total`]="{ item }">{{ item.total | numberFormat((round = true)) }}</template>
                  </v-data-table>
                </v-card-text>
              </v-card>
              <v-card flat tile v-if="dataframe.selected.length > 0" :loading="charts.profiles.data.length == 0">
                <v-divider />
                <v-toolbar flat tile dense>
                  <v-toolbar-title>Individual Well Profiles</v-toolbar-title>
                  <v-spacer/>
                  <template v-if="single && selected_well">
                    <dlg-edit-params :item="prediction" :well="selected_well" @submitted="onWellParams" />
                    <dlg-edit-well :item="prediction" :well="selected_well" :uniques="uniques" @submitted="onWellEdit" />
                    <v-btn icon @click="onCloseProfile"><v-icon>mdi-close</v-icon></v-btn>
                  </template>
                </v-toolbar>
                <v-card-text>
                  <vue-plotly
                    v-if="charts.profiles.data.length > 0"
                    :data="charts.profiles.data"
                    :layout="charts.profiles.layout"
                    :options="charts.profiles.options"
                    @click="onProfileClick"
                  />
                </v-card-text>
              </v-card>
              <v-card flat tile :loading="charts.typeCurves.data.length == 0">
                <v-divider />
                <v-toolbar flat tile dense>
                  <v-toolbar-title>Type Curves</v-toolbar-title>
                  <v-spacer/>
                  <!-- <ax-menu
                    icon="sigma"
                    title="Aggregates"
                    :options="aggregates.options"
                    :default="aggregates.selected"
                    model="aggregates.selected"
                    @changed="onMenuChange"
                  /> -->
                  <ax-button icon="mdi-export" tooltip="Export type curves" @clicked="onExportTypeCurves"></ax-button>
                </v-toolbar>
                <v-card-text>
                  <vue-plotly
                    v-if="charts.typeCurves.data.length > 0"
                    :data="charts.typeCurves.data"
                    :layout="charts.typeCurves.layout"
                    :options="charts.typeCurves.options"
                  />
                </v-card-text>
              </v-card>
            </template>
            <v-card-text v-else class="pa-16 grey--text" :class="[ isDark ? 'text--lighten': 'text--darken' ]">
              <p class="mx-16 mt-16 text-h4">Select a well or a group of wells</p>
              <p class="mx-16">To select a groups of wells&hellip;</p>
              <ul class="mx-16 mb-4">
                <li><v-icon>mdi-lasso</v-icon> Drag a shape around the wells of choice</li>
                <li><v-icon>mdi-checkbox-marked</v-icon> Select the checkbox next to each well in the table</li>
              </ul>
              <p class="mx-16">To select a single well&hellip;</p>
              <ul class="mx-16 mb-4">
                <li><v-icon>mdi-bullseye</v-icon> Click a well on the map</li>
                <li><v-icon>mdi-cursor-pointer</v-icon> Click a row in the table</li>
              </ul>
            </v-card-text>
          </v-col>
        </v-row>
      </v-card-text>
    </v-card>
    <dlg-open-file v-else mode="prediction" :name="application.name" @submitted="uuid=$event" />
  </v-container>
</template>
<script>
import Predictions from '@/api/predictions'
import DucsPermits from '@/api/ducspermits'
import XLSX from "xlsx"
import DataFrame from "dataframe-js"
import AxButton from '@/components/button'
import AxMenu from '@/components/menu'
import toolbar from '@/components/toolbar'
import dlgOpenFile from '@/components/open'
import dlgEditWell from '@/components/Apps/DucsPermits/EditWell'
import dlgEditParams from '@/components/Apps/DucsPermits/EditParams'
import dlgRenameGroup from '@/components/Apps/Forecast/Dialogs/RenameGroup'
import dlgFilterWells from '@/components/Apps/DucsPermits/FilterWells'
import VuePlotly from '@statnett/vue-plotly'
export default {
  name: "DucsPermits",
  props: ["application"],
  components: {
    AxButton, 
    AxMenu, 
    toolbar,
    dlgOpenFile,
    dlgEditWell,
    dlgEditParams,
    dlgFilterWells,
    dlgRenameGroup, 
    VuePlotly
  },
  data() {
    return {
      uuid: null,
      prediction: null,
      dataframe: {
        table: [],
        headers: [],
        selected: [],
        search: '',
      },
      unwatch: null,
      groups: {
        selected: undefined,
        counter: 0,
        list: []
      },
      targets: {
        selected: "oil",
        options: [
          { text: "Oil", value: "oil", units: "bbl", color: "#616161" },
          { text: "Gas", value: "gas", units: "mcf", color: "#4CAF50" },
          { text: "Water", value: "water", units: "bbl", color: "#03A9F4" }
        ]
      },
      method: 'multi3a',
      predictions: null,
      typeCurves: null,
      uniques: null,
      single: false,
      filters: {
        filterAll: false,
        options: {}
      },
      menu2: false,
      dimension: undefined,
      timescale: {
        selected: "relative",
        options: [
          { text: "Relative", value: "relative" },
          { text: "Absolute", value: "absolute" },
        ],
      },
      aggregates: {
        selected: "avg",
        options: [
          { text: "Average (Mean)", value: "avg" },
          { text: "Total (Sum)", value: "total" },
        ],
      },
      axis_types: {
        selected: "log",
        options: [
          { text: "Linear", value: "linear" },
          { text: "Log", value: "log" },
        ],
      },
      charts: {
        map: {
          data: [],
          layout: {
            autosize: true,
            hovermode: "closest",
            showlegend: false,
            height: 400,
            mapbox: {
              style: "mapbox://styles/schajee/cl1kethtn00sc14qo74wioif5",
              bearing: 0,
              center: {
                lat: 0,
                lon: 0
              },
              pitch: 0,
              zoom: 8
            },
            font: { family: "Roboto, sans-serif", color: '#808080' },
            margin: { l: 0, r: 0, b: 0, t: 0, pad: 0 },
            paper_bgcolor: "rgba(0,0,0,0)",
            plot_bgcolor: "rgba(0,0,0,0)"
          },
          options: {
            mapboxAccessToken: process.env.VUE_APP_MAPBOX_TOKEN,
            displayModeBar: true,
            displaylogo: false,
            modeBarButtonsToRemove: ["toImage", "toggleHover"],
            responsive: true
          }
        },
        profiles: {
          data: [],
          layout: {
            autosize: true,
            height: 250,
            legend: { x: 1, y: 1, xanchor: "right" },
            font: { family: "Roboto, sans-serif", color: '#808080' },
            hovermode: "closest",
            margin: { l: 70, t: 15, r: 15, b: 45 },
            xaxis: { title: { text: `Time (months)` }, type: "linear" },
            yaxis: { title: { text: `Predicted Production` }, type: "log" },
            paper_bgcolor: "rgba(0,0,0,0)",
            plot_bgcolor: "rgba(0,0,0,0)"
          },
          options: { displayModeBar: false, responsive: true }
        },
        typeCurves: {
          data: [], 
          layout: {
            autosize: true,
            height: 250,
            showlegend: true,
            legend: { x: 1, y: 1, xanchor: "right" },
            font: { family: "Roboto, sans-serif", color: '#808080' },
            xaxis: { title: { text: "Time (months)" }, dtick: 12 },
            yaxis: { title: { text: "Predicted Production" }, type: "log" },
            yaxis2: {
              overlaying: "y",
              side: "right",
              type: "log",
              showgrid: false,
              zeroline: false,
              showline: false,
              autotick: true,
              ticks: "",
              showticklabels: false
            },
            hovermode: "closest",
            margin: { l: 45, t: 15, r: 15, b: 45 },
            paper_bgcolor: "rgba(0,0,0,0)",
            plot_bgcolor: "rgba(0,0,0,0)"
          },
          options: { displayModeBar: false, responsive: true }
        }
      },
      colorscales: {
        colors: {
          value: "Portland",
          options: [
            { text: "Red-Blue", value: "RdBu" },
            { text: "Yellow-Red", value: "YlOrRd" },
            { text: "Yellow-Green-Blue", value: "YlGnBu" },
            { text: "Portland", value: "Portland" },
            { text: "Picnic", value: "Picnic" },
            { text: "Jet", value: "Jet" },
            { text: "Hot", value: "Hot" },
            { text: "Electric", value: "Electric" },
            { text: "Blue-Red", value: "Bluered" }
          ]
        },
        feature: {
          value: "oil",
          options: [
            { text: "Length of Production", value: "Months" },
            { text: "Total Production", value: "oil" }
            // { text: "Gas/Oil Ratio", value: "ratio" },
          ]
        }
      },
      colors: {
        curves: {
          p10: "#F44336",
          p50: "#4CAF50",
          p90: "#2196F3",
          avg: "#B0BEC5",
          total: "#B0BEC5",
          w10: "#F44336",
          w50: "#4CAF50",
          w90: "#2196F3"
        },
        lines: {
          history: "#BDBDBD",
          arps1: "#F44336",
          advanced3a: "#2196F3",
          advanced5: "#2196F3",
          multi3a: "#FF9800",
          multi5: "#FF9800",
          bfit: "#FF9800",
          hyper5: "#9C27B0",
          hyper5a: "#1B5E20",
        },
        text: {
          arps1: "error--text",
          advanced3a: "primary--text",
          advanced5: "primary--text",
          multi3a: "orange--text",
          multi5: "orange--text",
          hyper5: "purple--text",
          hyper5a: "green--text",
        }
      },
      statistics: {
        table: [],
        headers: [
          { text: 'N', value: 'n' }, 
          { text: 'P10 (bbl)', value: 'p10' }, 
          { text: 'P50 (bbl)', value: 'p50' }, 
          { text: 'P90 (bbl)', value: 'p90' }, 
          // { text: 'W10 (bbl)', value: 'w10' }, 
          // { text: 'W50 (bbl)', value: 'w50' }, 
          // { text: 'W90 (bbl)', value: 'w90' }, 
          { text: 'Average', value: 'avg' }, 
          { text: 'Swanson\'s Mean', value: 'total' }, 
        ]
      }
    }
  },
  watch: {
    uuid: function(val, old) {
      if (val) {
        Predictions.retrieve(val).then(response => {
          this.prediction = response.data
          this.loadData()
        })
      } else {
        this.clearData()
      }
    },
    "groups.selected": function(val, old) {
      if (val) {
        this.dataframe.selected = this.selectDataFrame('group')
      } else {
        this.dataframe.selected = this.selectDataFrame()
      }
    },
    dimension: function() {
      this.setTypeCurves()
    },
    "targets.selected": function() {
      this.plotMap()
      this.fetchWellProfiles().then(response => {
        this.predictions = JSON.parse(response.data)
        this.plotProfiles()
      })
      this.setTypeCurves()
    },
    "timescale.selected": function() {
      if (this.timescale.selected == 'absolute') {
        this.charts.profiles.layout.xaxis.type = 'date'
      } else {
        this.charts.profiles.layout.xaxis.type = 'linear'
      }
      this.plotProfiles()
      this.setTypeCurves()
    },
    "aggregates.selected": function() {
      this.plotTypeCurves()
    },
    "axis_types.selected": function(val) {
      this.charts.profiles.layout.yaxis.type = val
      this.charts.typeCurves.layout.yaxis.type = val
      this.charts.typeCurves.layout.yaxis2.type = val
    },
    'isDark': function(val) {
      this.toggleDarkMode(val)
    }
  },
  computed: {
    wells() {
      return this.dataframe.selected.map(w => w[this.prediction.config.api].toString())
    },
    wells_n() {
      return this.dataframe.selected.length
    },
    wells_t() {
      return this.dataframe.table.length
    },
    well_api() {
      return this.selected_well[this.prediction.config.api]
    },
    selected_well() {
      if (this.wells_n == 1) {
        return this.dataframe.selected[0]
      }
      return undefined
    },
    selectedGroup() {
      return this.groups.list.find(group => group.id == this.groups.selected)
    },
    targetIcon() {
      return this.targets.selected == "oil" ? "tint" : this.targets.selected == "water" ? "water" : "burn"
    },
    dimensions() {
      let dim = this.filterObject(this.filters.options, "type", "object")
      // dim.unshift({ text: "Methods", value: "methods" })
      return dim
    },
    minStartDate() {
      return new Date(Math.min.apply(null, ([... new Set($vm.dataframe.table.map(w => w['StartDate']))].map(w => new Date(w)))))
    }
  },
  methods: {
    loadData() {
      DucsPermits.frame(this.uuid).then(response => {
        let frame = JSON.parse(response.data)
        let exclude = ['index', 'Latitude', 'Longitude', 'Surface Latitude', 'Surface Longitude']
        this.dataframe.headers = frame.schema.fields.filter(h => !exclude.includes(h.name)).map(function(h) {
          return { text: h.name, value: h.name, filterable: true }
        })
        // this.dataframe.headers.push(
        //   { text: 'Actions', value: 'actions', sortable: false },
        // )
        this.dataframe.table = frame.data
        // Stringfy every API
        this.dataframe.table.forEach(
          well => (well[this.prediction.config.api] = String(well[this.prediction.config.api]))
        )
        this.plotMap()
        this.unwatch = this.$watch("dataframe.selected", function(val, old) {
          if (val.length > 0) {
            this.selectPointsOnMap()
            this.plotProfiles()
            this.setTypeCurves()
            this.toggleDarkMode(this.isDark)
          } else {
            this.unSelectPointsOnMap()
          }
        })
      })
      this.fetchWellProfiles().then(response => {
        this.predictions = JSON.parse(response.data)
      })
      // DucsPermits.frame(this.uuid, { target: this.targets.selected, method: this.method }).then(response => {
      //   this.predictions = JSON.parse(response.data)
      // })
      // DucsPermits.typeCurves(this.uuid, { target: this.targets.selected, method: this.method }).then(response => {
      //   this.typeCurves = response.data
      // })
      DucsPermits.uniques(this.uuid).then(response => this.uniques = response.data )
      DucsPermits.filters(this.uuid).then(response => {
        let exclusion = [this.prediction.config['latitude'], this.prediction.config['longitude']]
        this.filters.options = Object.fromEntries(Object.entries(response.data).filter(([key,value]) => !exclusion.includes(key)))
        for (let [filter, value] of Object.entries(this.filters.options)) {
          if (["object", "datetime64[ns]"].includes(value.type)) this.filters.options[filter].selected = []
          else this.filters.options[filter].selected = [value.min, value.max]
          this.filters.options[filter].enabled = false
        }
      })
    },
    clearData() {
      this.prediction = null
      this.dataframe.table = []
      this.dataframe.selected = this.selectDataFrame()
      this.statistics.table = []
      this.groups.list = []
      this.groups.selected = undefined
      this.single = false
      this.charts.map.data = []
      this.charts.profiles.data = []
      this.charts.typeCurves.data = []
      this.predictions = null
      this.typeCurves = null
      this.unwatch()
    },
    getMapObject(items, params, colors) {
      const feature = this.targets.selected
      const color   = colors.colors.value
      return {
        type: "scattermapbox",
        text: items.map(x => x[params.api]),
        lat: items.map(x => x[params.latitude]),
        lon: items.map(x => x[params.longitude]),
        mode: "markers",
        marker: {
          size: 6,
          reversescale: true,
          autocolorscale: false,
          color: items.map(x => x[feature]),
          colorscale: color,
          cmin: Math.min(...items.map(x => x[this.capitalize(feature)])),
          cmax: Math.max(...items.map(x => x[this.capitalize(feature)])),
          colorbar: {
            thickness: 10,
            titleside: "right",
            outlinecolor: "rgba(255, 255, 255, 0)",
            ticks: "outside",
            ticklen: 3,
            shoticksuffix: "last",
            ticksuffix: "",
            dtick: 100000,
          },
        },
        selected: {
          marker: {
            size: 12,
            color: '#F44336',
          }
        }
      }
    },
    plotMap() {
      if (this.dataframe.table.length) {
        this.charts.map.data = [this.getMapObject(this.dataframe.table, this.prediction.config, this.colorscales, false)]
        // Set map style
        this.charts.map.layout.mapbox.style = this.isDark ? 'mapbox://styles/schajee/cla6nsgx3002315o16d3x006u' : 'mapbox://styles/schajee/cla6np60a001z14mjo36g7dv5'
        // Center align map
        this.charts.map.layout.mapbox.center.lat = this.mean(this.dataframe.table.map(x => x[this.prediction.config.latitude]))
        this.charts.map.layout.mapbox.center.lon = this.mean(this.dataframe.table.map(x => x[this.prediction.config.longitude]))
      }
    },
    plotProfiles() {
      if (!this.isEmpty(this.predictions)) {
        if (this.wells_n > 0) {
          let plots = []
          this.charts.profiles.data = []
          Object.entries(this.predictions).forEach(([api, data]) => {
            if (this.wells.includes(api)) {
              plots.push({
                name: api,
                type: "scatter",
                mode: 'lines',
                showlegend: false,
                x: this.timescale.selected == 'absolute' ? this.create_date_array(this.wellStartDate(api), 24) : this.create_array(1, 24),
                y: Object.values(data),
                line: { shape: 'spline', dash: "solid", width: 1 },
              })
            }
          })
          this.charts.profiles.data = plots
        }
      }
    },
    onRowClick(well) {
      if (well) {
        this.single = true
        this.dataframe.selected = [well]
      }
    },
    onMapClick(event) {
      if (event) {
        this.setSelectedWells(event.points[0].text)
        this.$set(this.charts.map.data[0], 'selectedpoints', [event.points[0].pointNumber])
        this.single = true
      } 
    },
    onMapSelect(event) {
      if (event) {
        if (event.points.length > 0) {
          this.setSelectedWells(event.points.map(p => p.text))
          this.single = false
        }
      }
    },
    onMapDeselect(event) {
      if (event == null) {
        this.dataframe.selected = this.selectDataFrame()
        this.unSelectPointsOnMap()
        this.single = false
      }
    },
    onProfileClick(e) {
      this.setSelectedWells(e.points[0].data.name)
      this.single = true
    },
    onCloseProfile() {
      this.single = false
      if (this.groups.selected) {
        this.dataframe.selected = this.selectDataFrame('group')
        this.plotProfiles()
        this.setTypeCurves()
      } else {
        this.dataframe.selected = this.selectDataFrame()
      }
    },
    setSelectedWells(input) {
      let wells = []
      if (input) {
        if (Array.isArray(input)) {
          wells = this.dataframe.table.filter(w => input.includes(w[this.prediction.config.api]))
        } else {
          wells = this.dataframe.table.filter(w => w[this.prediction.config.api] == input)
        }
        this.dataframe.selected = wells
      }
    },
    plotTypeCurves() {
      if (this.typeCurves) {
        let plots = []
        this.charts.typeCurves.data = []
        this.typeCurves.overall.curves.index.forEach((key, value) => {
          if (['avg','total'].includes(key)) {
            if (key == this.aggregates.selected) {
              plots.push({
                name: this.capitalize(key),
                type: "bar",
                y: this.typeCurves.overall.curves.data[value],
                yaxis: 'y2',
                marker: { color: this.colors.curves[key], opacity: 0.5 }
              })
            }
          } else {
            plots.push({
              name: this.capitalize(key),
              type: "scatter",
              mode: 'lines',
              y: this.typeCurves.overall.curves.data[value],
              line: { shape: 'spline', dash: key.startsWith('w') ? 'dot' : 'solid', width: 1, color: this.colors.curves[key] },
            })
          }
        })
        this.charts.typeCurves.data = plots
      }
    },
    onMenuChange(value, variable) {
      this.setPath(this, variable, value)
    },
    onWellEdit(payload, predictions) {
      this.dataframe.table.find(well => well[this.prediction.config.api] == this.well_api)['StartDate'] = payload['start']
      this.predictions[this.well_api] = predictions[this.targets.selected][this.method]['predictions']
      this.plotProfiles()
      this.setTypeCurves()
    },
    onWellParams() {
      this.setTypeCurves()
    },
    typeEUR(curve, filter=undefined) {
      let index = []
      let sum = 0
      let mean = 0

      if (this.typeCurves) {
        if (this.dimension == undefined) {
          index = this.typeCurves.overall.curves.index.indexOf(curve)
          sum = this.sum(this.typeCurves.overall.curves.data[index])
          mean = this.mean(this.typeCurves.overall.curves.data[index])
        } else {
          index = this.typeCurves.grouped[filter].index.indexOf(curve)
          sum = this.sum(this.typeCurves.grouped[filter].data[index])
          mean = this.mean(this.typeCurves.grouped[filter].data[index])
        }
      }
      return sum
    },
    plotStatistics() {
      let row = {}
      this.statistics.table = []
      
      if (!!this.dimension) {
        if (!this.statistics.headers.find(h => h.value == "d")) this.statistics.headers.unshift({ text: this.dimension, value: "d" })
        this.filters.options[this.dimension].list.forEach(filter => {
          row = this.getStatistic(filter)
          if (!this.isEmpty(row)) this.statistics.table.push(row)
        })
      } else {
        this.statistics.headers = this.statistics.headers.filter(h => h.value !== "d")
        row = this.getStatistic()
        if (!this.isEmpty(row)) this.statistics.table.push(row)
      }
    },
    getStatistic(filter=undefined) {
      let row = {}
      let filtered = this.dataframe.selected
      if (!!filter) {
        filtered = filtered.filter(well => well[this.dimension] == filter)
      }
      if (filtered.length > 0) {
        this.statistics.headers.forEach(h => {
          if (h.value == 'd') {
            row[h.value] = String(filter)
          } else if (h.value == 'n') {
            row[h.value] = filtered.length
          } else if (h.value == 'total') {
            h.text = 'Swanson\'s Mean'
            row[h.value] = 0.3 * row["p10"] + 0.4 * row["p50"] + 0.3 * row["p90"]
          } else {
            h.text = `${this.capitalize(h.value)} (bbl)`
            row[h.value] = this.typeEUR(h.value, filter)
          }
        })
      } 
      return row
    },
    fetchWellProfiles() {
      return DucsPermits.production(this.uuid, { 
        target: this.targets.selected, 
        method: this.method 
      })
    },
    fetchTypeCurves() {
      return DucsPermits.typeCurves(this.uuid, { 
        target: this.targets.selected, 
        method: this.method, 
        wells: (this.wells_n == this.wells_t || this.wells_n == 0) ? null : this.wells,
        dimension: this.dimension,
        timescale: this.timescale.selected
      })
    },
    setTypeCurves() {
      this.fetchTypeCurves().then(response => {
        this.typeCurves = response.data
        this.typeCurves.overall.curves = JSON.parse(this.typeCurves.overall.curves)
        if ('grouped' in this.typeCurves && !this.isEmpty(this.typeCurves.grouped)) {
          Object.entries(this.typeCurves.grouped).forEach(([key, value]) => {
            this.typeCurves.grouped[key] = JSON.parse(value)
          })
          // this.typeCurves.grouped.curves = JSON.parse(this.typeCurves.grouped.curves)
        }
        this.plotTypeCurves()
        this.plotStatistics()
      })
    },
    onFilterWells(filters) {
      this.filters = filters

      // See if there is a need to filter
      filters = Object.fromEntries(Object.entries(filters.options).filter(([k, v]) => v.enabled))

      if (!this.isEmpty(filters)) {
        // Remove single well selection
        this.single = false

        // Filter wells from datatable if any filter matches conditions
        let wells = this.dataframe.table.filter(
          function(well) {
            let include = {}
            for (let [column, value] of Object.entries(filters)) {
              include[column] = false
              if (value.type == "datetime64[ns]") {
                // Check if date(value) exists in list
                if (value.selected.includes(parseInt(well[column].substr(0, 4)))) include[column] = true
              } else if (["int8", "float8", "int16", "float16", "int32", "float32", "int64", "float64"].includes(value.type)) {
                if (value.selected[0] <= well[column] && well[column] <= value.selected[1]) include[column] = true
              } else {
                if (value.selected.includes(well[column])) include[column] = true
              }
            }
            if (this.filters.filterAll) return Object.values(include).every(item => item === true)
            else return Object.values(include).some(item => item === true)
          }.bind(this)
        )
        wells = wells.map(w => w[this.prediction.config.api])
        this.dataframe.selected = this.selectDataFrame(wells)
      }
    },
    onDeleteGroup(id) {
      this.groups.list = this.groups.list.filter(group => group.id !== id)
      if (this.groups.selected == id) this.groups.selected = undefined
    },
    onSaveGroup() {
      // Group is selected - overwrite group
      if (!!this.groups.selected) {
        if (this.groups.selected > 0) this.selectedGroup.wells = this.wells.slice()
      // Search is not empty - create group from search
      } else {
        this.groups.counter += 1
        this.groups.list.push({
          id: this.groups.counter,
          name: `Group ${this.groups.counter}`,
          wells: this.dataframe.selected.map(w => w[this.prediction.config.api])
        })
        this.groups.selected = this.groups.counter
      }
    },
    onRenameGroup(group) {
      if (this.selectedGroup) {
        this.selectedGroup.name = group.name
      }
    },
    onAddWell() {

    },
    onRemoveWell() {

    },
    selectPointsOnMap() {
      if (!!this.groups.selected) {
        this.$set(this.charts.map.data[0], 'selectedpoints', this.charts.map.data[0].text.map((e, i) => (this.selectedGroup.wells.includes(e) ? i : "")).filter(String))
      } else if (this.dataframe.selected.length > 0) {
        this.$set(this.charts.map.data[0], 'selectedpoints', this.charts.map.data[0].text.map((e, i) => (this.wells.includes(e) ? i : "")).filter(String))
      }
    },
    unSelectPointsOnMap() {
      this.$set(this.charts.map.data[0], 'selectedpoints', [])
    },
    selectDataFrame(criteria) {
      let output = []
      if (criteria == undefined) {
        // 
      } else if (Array.isArray(criteria)) {
        output = this.dataframe.table.filter(well => criteria.includes(well[this.prediction.config.api]))
      } else {
        if (criteria == "group") {
          output = this.dataframe.table.filter(well => this.selectedGroup.wells.includes(well[this.prediction.config.api]))
        } else if (criteria == "single") {
          output = this.dataframe.table.filter(well => well[this.prediction.config.api] == this.selected_well)
        } else {
          output = this.dataframe.table
        }
      }
      return output
    },
    customFilter(value, search, item) {
      if (value != null && search != null && search == "group" && this.wells.includes(value.toString())) return true
      if (value != null && search != null && value.toString().indexOf(search) !== -1) return true
      return false
    },
    onMenuChange(value, variable) {
      this.setPath(this, variable, value)
    },
    onExportTypeCurves() {
      var wb = XLSX.utils.book_new()

      // TAB1 - Volumes
      var curves = {
        P10: this.typeCurves.overall.curves.data[this.typeCurves.overall.curves.index.indexOf('p10')],
        P50: this.typeCurves.overall.curves.data[this.typeCurves.overall.curves.index.indexOf('p50')],
        P90: this.typeCurves.overall.curves.data[this.typeCurves.overall.curves.index.indexOf('p90')]
      }
      
      var cdf = new DataFrame(curves)
      var tab1 = [cdf.listColumns(), ...cdf.toArray()]
      var cws = XLSX.utils.aoa_to_sheet(tab1)
      XLSX.utils.book_append_sheet(wb, cws, "Volumes")

      // TAB2 - Parameters
      var params = {
        Parameter: ["q", "d", "b"],
        P10: [this.typeCurves.overall.params.P10.qi, this.typeCurves.overall.params.P10.Di, this.typeCurves.overall.params.P10.b],
        P50: [this.typeCurves.overall.params.P50.qi, this.typeCurves.overall.params.P50.Di, this.typeCurves.overall.params.P50.b],
        P90: [this.typeCurves.overall.params.P90.qi, this.typeCurves.overall.params.P90.Di, this.typeCurves.overall.params.P90.b]
      }
      var pdf = new DataFrame(params)
      var tab2 = [pdf.listColumns(), ...pdf.toArray()]
      var pws = XLSX.utils.aoa_to_sheet(tab2)
      XLSX.utils.book_append_sheet(wb, pws, "Parameters")

      // // TAB3 - Summary Stats
      // var sdf = new DataFrame(this.statistics.items)
      // var tab3 = [sdf.listColumns(), ...sdf.toArray()]
      // var sws = XLSX.utils.aoa_to_sheet(tab3)
      // XLSX.utils.book_append_sheet(wb, sws, "Statistics")

      // TAB4 - List of APIs
      var adf = new DataFrame({ API: this.wells })
      var tab4 = [adf.listColumns(), ...adf.toArray()]
      var aws = XLSX.utils.aoa_to_sheet(tab4)
      XLSX.utils.book_append_sheet(wb, aws, "APIs")

      var filename = `${this.uuid} - Type Curves.xlsb`
      // Return binary XLS format (smaller)
      XLSX.writeFile(wb, filename)
    },
    onEditWell(well) {
      alert('editing')
    },
    onDeleteWell(well) {
      alert('deleting')

    },
    wellStartDate(api) {
      return new Date(this.dataframe.table.find(w => w[this.prediction.config.api] == api)['StartDate'])
    },
    toggleDarkMode(val) {
      if (val) {
        this.$set(this.charts.map.layout.mapbox, "style", "mapbox://styles/schajee/cla6nsgx3002315o16d3x006u")
        this.$set(this.charts.typeCurves.layout.yaxis, 'gridcolor', '#111')
        this.$set(this.charts.profiles.layout.yaxis, 'gridcolor', '#111')
        this.$set(this.charts.profiles.layout.xaxis, 'gridcolor', '#111')
      } else {
        this.$set(this.charts.map.layout.mapbox, "style", "mapbox://styles/schajee/cla6np60a001z14mjo36g7dv5")
        this.$set(this.charts.typeCurves.layout.yaxis, 'gridcolor', '#eee')
        this.$set(this.charts.profiles.layout.yaxis, 'gridcolor', '#eee')
        this.$set(this.charts.profiles.layout.xaxis, 'gridcolor', '#eee')
      }
    }
  }
}
</script>